import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Flag, CheckCircle, Trash2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminReports({ embedded = false }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/reports`);
      setReports(response.data);
    } catch (err) {
      setError('Erreur lors du chargement des signalements');
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (reportId) => {
    try {
      await axios.patch(`${API}/admin/reports/${reportId}/resolve`);
      loadReports();
    } catch (err) {
      alert('Erreur lors de la résolution du signalement');
    }
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce signalement ?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/reports/${reportId}`);
      loadReports();
    } catch (err) {
      alert('Erreur lors de la suppression du signalement');
    }
  };

  const getProblemTypeLabel = (type) => {
    const labels = {
      'lien_casse': 'Problème avec le lien',
      'publicites': 'Trop de publicités',
      'mauvais_contenu': 'Contenu incorrect'
    };
    return labels[type] || type;
  };

  const getProblemTypeColor = (type) => {
    const colors = {
      'lien_casse': 'bg-red-900/30 text-red-200 border-red-500/50',
      'publicites': 'bg-yellow-900/30 text-yellow-200 border-yellow-500/50',
      'mauvais_contenu': 'bg-orange-900/30 text-orange-200 border-orange-500/50'
    };
    return colors[type] || 'bg-gray-900/30 text-gray-200 border-gray-500/50';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-white font-work">Chargement...</div>
      </div>
    );
  }

  const unresolvedReports = reports.filter(r => !r.resolved);
  const resolvedReports = reports.filter(r => r.resolved);

  return (
    <div className={embedded ? "" : "min-h-screen bg-bg-primary p-8"}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Unresolved Reports */}
        <Card className="bg-bg-secondary border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white font-work flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                Signalements Non Résolus ({unresolvedReports.length})
              </CardTitle>
              <Button onClick={loadReports} variant="outline" size="sm" className="border-white/20">
                Actualiser
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {unresolvedReports.length === 0 ? (
              <p className="text-text-secondary font-inter text-center py-8">
                Aucun signalement non résolu
              </p>
            ) : (
              <div className="space-y-3">
                {unresolvedReports.map(report => (
                  <div key={report.id} className={`p-4 border rounded-lg ${getProblemTypeColor(report.problem_type)}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Flag className="w-4 h-4" />
                          <h3 className="font-work font-semibold text-white">
                            {report.content_title}
                            {report.content_type === 'series' && report.season_number && report.episode_number && (
                              <span className="ml-2 text-sm opacity-80">
                                S{report.season_number}E{report.episode_number}
                              </span>
                            )}
                          </h3>
                        </div>
                        
                        <div className="space-y-1 text-sm">
                          <p className="font-semibold">{getProblemTypeLabel(report.problem_type)}</p>
                          <p className="opacity-80">{report.message}</p>
                          <div className="flex gap-4 text-xs opacity-70 mt-2">
                            <span>Par: {report.user_email}</span>
                            <span>Le: {new Date(report.created_at).toLocaleString('fr-FR')}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleResolve(report.id)}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Résoudre
                        </Button>
                        <Button
                          onClick={() => handleDelete(report.id)}
                          size="sm"
                          variant="outline"
                          className="border-red-500/50 text-red-200 hover:bg-red-900/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Resolved Reports */}
        {resolvedReports.length > 0 && (
          <Card className="bg-bg-secondary border-white/10">
            <CardHeader>
              <CardTitle className="text-white font-work flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Signalements Résolus ({resolvedReports.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {resolvedReports.map(report => (
                  <div key={report.id} className="p-3 bg-green-900/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-white font-work font-semibold text-sm">
                          {report.content_title}
                          {report.content_type === 'series' && report.season_number && report.episode_number && (
                            <span className="ml-2 opacity-80">
                              S{report.season_number}E{report.episode_number}
                            </span>
                          )}
                        </p>
                        <p className="text-text-secondary text-xs">
                          {getProblemTypeLabel(report.problem_type)} - {report.user_email}
                        </p>
                        <p className="text-text-secondary text-xs opacity-70">
                          Résolu le: {new Date(report.resolved_at).toLocaleString('fr-FR')}
                        </p>
                      </div>
                      <Button
                        onClick={() => handleDelete(report.id)}
                        size="sm"
                        variant="ghost"
                        className="text-red-200 hover:bg-red-900/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
